import { Box, ChakraProps, Flex, Text } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { MouseEventHandler, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ArrowRightIcon } from 'ui-components/dist/ArrowRightIcon';
import { Button } from 'ui-components/dist/Button';
import { FEATURE_FLAGS } from '~/constants';
import { generatePath, routes } from '~/core/router';
import { useFetchCreatorContent } from '~/hooks/api/useFetchCreatorContent';
import { useGetSelectedContents } from '~/hooks/api/useGetSelectedContents';
import {
  CONTENT_SEARCH_ORDER_BY,
  CONTENT_SEARCH_ROSTER,
} from '~/hooks/useContentSearch';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { useTalentManagerAccess } from '~/hooks/useTalentManagerAccess';
import { Carousel } from '~/screens/MyAgency/components/StackView/Carousel';
import { useSelectCreatorContentStore } from '~/screens/MyAgency/store';
import { mapApiSearchContentResultsFromApiToVm } from '~/searchApi';
import { selectTalentManager } from '~/selectors/talent-manager';
import { ICreator } from '~/types';
import { useCurrentUserAccess } from '~/userManagementApi/userAccess';
import { MeResponse } from '~/userManagementApi/userAccess.types';

import { myAgencyCreatorCardTrackId } from './CreatorInfo/constants';

export const LatestSearchContent: React.FC<{
  creator: ICreator;
  isShared?: boolean;
  btnDivProps?: ChakraProps;
  showActionButtons?: boolean;
  highlightContentOnly?: boolean;
  onOpenContentModal?: (creatorId: string) => void;
  onEditContentModal?: (creatorId: string, existingContent: any[]) => void;
}> = ({
  creator,
  isShared,
  btnDivProps,
  showActionButtons,
  highlightContentOnly,
  onOpenContentModal,
  onEditContentModal,
}) => {
  const { push } = useRouter();
  const { onModalOpen } = useSelectCreatorContentStore();
  const manager = useSelector(selectTalentManager);
  const userAccessData = useCurrentUserAccess({
    entityId: manager?.agency?.id,
  });

  // Feature flag for saved lists highlighted content
  const { isEnabled: isNewContentModalEnabled } = useFeature(
    FEATURE_FLAGS.foamSavedListsHighlightedContent,
  );

  // With Search API we only have the content ids, so we need to fetch the content data
  const searchContentHighlighted = useGetSelectedContents(
    creator.highlightedContent,
  );
  const searchContentAgencySelected = useGetSelectedContents(
    creator.contentAgencySelected,
  );
  // <--

  const fetchingContent =
    searchContentAgencySelected.isFetching ||
    searchContentHighlighted.isFetching;
  const hasAgencyContent = !!searchContentAgencySelected.data;
  const hasHighlightedContent = !!searchContentHighlighted.data;

  const creatorContent = useFetchCreatorContent({
    creatorId: creator.id,
    creatorSlug: creator.slug,
    isEnabled: !hasAgencyContent && !hasHighlightedContent,
  });

  const mappedCreatorContent = useMemo(() => {
    if (creatorContent.data) {
      return mapApiSearchContentResultsFromApiToVm(creatorContent.data.results);
    }
    return [];
  }, [creatorContent.data]);

  const { shouldHaveAccessAsManagerComanagerAdmin } = useTalentManagerAccess(
    creator,
    userAccessData?.data as MeResponse,
  );

  const canSelectHighlightedContent = useMemo(() => {
    return !isShared // manager can access the content picker if it's not shared
      ? shouldHaveAccessAsManagerComanagerAdmin // managers, comanagers (also external) and admins within the same agency can access content picker
      : false;
  }, [isShared, shouldHaveAccessAsManagerComanagerAdmin]);

  // Control of View more posts, compatible with Talent tab: if onOpenContentModal exists, we're in SavedListsView context
  const isInSavedListsContext = Boolean(onOpenContentModal);

  // Conditional logic for "View More Posts" button visibility
  const shouldShowViewMorePosts = useMemo(() => {
    if (isShared) return false; // Never show if shared

    if (isInSavedListsContext) {
      // In SavedListsView: only show when FF is OFF
      return !isNewContentModalEnabled;
    }

    // In other views (MyAgency/Talent tab): always show regardless of FF
    return true;
  }, [isShared, isInSavedListsContext, isNewContentModalEnabled]);

  const handleOpen = useCallback(() => {
    if (onOpenContentModal && isNewContentModalEnabled) {
      onOpenContentModal(creator.id);
    } else {
      onModalOpen(creator);
    }
  }, [onOpenContentModal, creator.id, isNewContentModalEnabled, onModalOpen]);

  const handleEdit = useCallback(() => {
    if (onEditContentModal && (hasAgencyContent || hasHighlightedContent)) {
      // Get existing content for editing
      const existingContent = hasHighlightedContent
        ? searchContentHighlighted.data
        : searchContentAgencySelected.data;
      onEditContentModal(creator.id, existingContent || []);
    } else {
      onModalOpen(creator);
    }
  }, [
    onEditContentModal,
    creator.id,
    hasAgencyContent,
    hasHighlightedContent,
    searchContentHighlighted.data,
    searchContentAgencySelected.data,
    onModalOpen,
  ]);

  const redirectToContentSearch: MouseEventHandler<HTMLButtonElement> =
    useCallback(
      (e) => {
        e.stopPropagation();
        const contentSearchUrl = generatePath(routes.contentSearch, {
          query: {
            view: 'grid',
            creator: creator.id,
            page: '1',
            roster: CONTENT_SEARCH_ROSTER.agency,
            sortBy: CONTENT_SEARCH_ORDER_BY.publishedAt,
          },
        });
        push(contentSearchUrl);
      },
      [creator.id, push],
    );

  const contents = useMemo(() => {
    // if creator has highlighted content, we ensure that assets are present to avoid page fails in the carrousel
    if (hasHighlightedContent) {
      const hasAssets = searchContentHighlighted.data.every((item: any) =>
        Object.prototype.hasOwnProperty.call(item, 'assets'),
      );

      return hasAssets ? searchContentHighlighted.data : [];
    }

    // if creator has agency content, we ensure that assets are present to avoid page fails in the carrousel
    if (hasAgencyContent) {
      const hasAssets = searchContentAgencySelected.data.every((item: any) =>
        Object.prototype.hasOwnProperty.call(item, 'assets'),
      );

      return hasAssets ? searchContentAgencySelected.data : [];
    }

    return (!fetchingContent && mappedCreatorContent) || [];
  }, [
    mappedCreatorContent,
    hasAgencyContent,
    hasHighlightedContent,
    searchContentAgencySelected.data,
    searchContentHighlighted.data,
    fetchingContent,
  ]);

  const boxHeight = useMemo(() => {
    if (!creatorContent.isLoading && contents.length === 0) return '0px';
    return '286px';
  }, [contents, creatorContent]);

  if (highlightContentOnly && !searchContentAgencySelected.data) {
    // for highlighted only content we don't show anything if creator has no highlighted content
    return null;
  }

  return (
    <>
      {contents.length !== 0 ? (
        <Box
          margin={6}
          marginRight={{
            base: 6,
            sm: 0,
          }}
          marginTop={{
            base: 4,
            sm: 0,
          }}
          height={boxHeight}
          track-id={myAgencyCreatorCardTrackId.content}
        >
          <Flex
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            marginBottom={2}
          >
            <Text
              fontSize="2xl"
              fontWeight="500"
              color="primary.600"
              paddingBottom={4}
            >
              {hasAgencyContent || hasHighlightedContent
                ? 'Highlighted Content'
                : 'Latest content'}
            </Text>
            {showActionButtons && (
              <Flex {...btnDivProps} marginBottom={2} gap={2}>
                {canSelectHighlightedContent && (
                  <>
                    <Button
                      size={'sm'}
                      trackId="foam-myagency-stackview-edit-content"
                      variant="tertiary"
                      sx={{
                        textTransform: 'initial',
                      }}
                      onClick={
                        hasAgencyContent || hasHighlightedContent
                          ? handleEdit
                          : handleOpen
                      }
                      _hover={{ backgroundColor: 'primary.100' }}
                      _focusVisible={{ backgroundColor: 'primary.100' }}
                      _active={{ backgroundColor: 'primary.200' }}
                    >
                      Select Highlighted Content
                    </Button>
                  </>
                )}
                {shouldShowViewMorePosts && (
                  <Button
                    size={'sm'}
                    rightIcon={<ArrowRightIcon boxSize="18px" />}
                    trackId="foam-talent-profile-more-posts"
                    variant="tertiary"
                    sx={{
                      textTransform: 'initial',
                    }}
                    onClick={redirectToContentSearch}
                    _hover={{ backgroundColor: 'primary.100' }}
                    _focusVisible={{ backgroundColor: 'primary.100' }}
                    _active={{ backgroundColor: 'primary.200' }}
                  >
                    View more posts
                  </Button>
                )}
              </Flex>
            )}
          </Flex>
          <Carousel contents={contents || []} showPlatformIcon />
        </Box>
      ) : null}
    </>
  );
};
